import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import { useSession } from "../auth/use-session.js";

// 1.D.9 Onboard-naekyma. Ekana kayttajana onboard tekee minusta managerin.
// Kun jokin toinen on jo onboardattu, sivu redirectaa /:iin (tai
// /login:iin jos ei kirjautunut).

export type OnboardOutcome =
  | { kind: "loading" }
  | { kind: "success"; message: string }
  | { kind: "already_self"; message: string }
  | { kind: "already_other"; message: string }
  | { kind: "unauthenticated" }
  | { kind: "error"; message: string };

/**
 * Puhdas helper: muunna API-vastaus / ApiError UI-tilaksi.
 * Yksikkotestattava ilman DOMia.
 */
export function interpretOnboard(
  result: { alreadyOnboarded: boolean; role: string } | ApiError
): OnboardOutcome {
  if (result instanceof NotAuthenticatedError) return { kind: "unauthenticated" };
  if (result instanceof ApiError) {
    if (result.status === 409) {
      return {
        kind: "already_other",
        message: "Toinen käyttäjä on jo onboardattu adminiksi.",
      };
    }
    return { kind: "error", message: result.message };
  }
  if (result.alreadyOnboarded) {
    return { kind: "already_self", message: "Olet jo admin." };
  }
  return {
    kind: "success",
    message: `Tervetuloa! Saat rooliksi ${result.role}.`,
  };
}

export function Onboard() {
  const session = useSession();
  const [, navigate] = useLocation();
  const [outcome, setOutcome] = useState<OnboardOutcome>({ kind: "loading" });

  useEffect(() => {
    if (session.isPending) return;
    if (!session.isLoggedIn) {
      navigate("/login");
      return;
    }
    let cancelled = false;
    apiClient
      .postOnboard()
      .then((res) => {
        if (cancelled) return;
        setOutcome(interpretOnboard(res));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) setOutcome(interpretOnboard(err));
        else
          setOutcome({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [session.isPending, session.isLoggedIn, navigate]);

  // Already_other → odota sekunti ja redirect /:iin jotta kayttaja ehtii lukea
  useEffect(() => {
    if (outcome.kind !== "already_other") return;
    const t = setTimeout(() => navigate("/"), 2000);
    return () => clearTimeout(t);
  }, [outcome, navigate]);

  return (
    <section className="mx-auto max-w-md px-6 py-16">
      <h2 className="text-2xl font-semibold tracking-tight">Onboardaus</h2>
      <div className="mt-6 rounded-md border border-slate-800 bg-slate-900/40 px-4 py-4 text-sm">
        {outcome.kind === "loading" && <p className="text-slate-400">Käsitellään…</p>}
        {outcome.kind === "success" && <p className="text-emerald-200">{outcome.message}</p>}
        {outcome.kind === "already_self" && <p className="text-emerald-200">{outcome.message}</p>}
        {outcome.kind === "already_other" && (
          <p className="text-amber-200">{outcome.message} Siirrytään etusivulle hetken kuluttua…</p>
        )}
        {outcome.kind === "unauthenticated" && (
          <p className="text-slate-400">Sinut ohjataan kirjautumissivulle…</p>
        )}
        {outcome.kind === "error" && <p className="text-red-200">{outcome.message}</p>}
      </div>
    </section>
  );
}
